import * as React from 'react';
import { InputControlContext } from '../common/input-control-context/input-control-context';
import { DebounceTextarea } from '../common/debounce-textarea/debounce-textarea';
import { SingleValueComponent } from 'ui/common/types';

/**
 * The Component allows you to enter multiline text. It may be configured to grow in height automatically as the user enters more text.
 *
 * > It supports common InputControlContext component.
 */
export interface TextareaProps extends SingleValueComponent<string> {
  /**
   * Define max length of content text. Default is unlimited.
   */
  maxLength?: number;
  /**
   * Define amount of rows. Default is 1.
   */
  rows?: number;
  /**
   * Define whether component is expandable. Default is disabled.
   */
  autoExpandable?: boolean;
  /**
   * The number of milliseconds to delay onChange event. Default is 500.
   */
  wait?: number;
  /**
   * Keyboard event handler.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export class Textarea extends React.Component<TextareaProps> {
  public static defaultProps: Partial<TextareaProps> = {
    wait: 500,
    rows: 1,
    autoExpandable: true
  };

  private onFocus = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const currentTarget = e.currentTarget;
    // eslint:disable-next-line:no-unused-expression
    this.props.onFocus && this.props.onFocus(currentTarget.offsetLeft);
  };

  private onBlur = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const currentTarget = e.currentTarget;
    this.props.onBlur && this.props.onBlur(currentTarget.value);
  };

  public render() {
    const {
      autoExpandable,
      maxLength,
      readOnly,
      placeholder,
      rows,
      focus,
      value = '',
      wait,
      showAIChatIcon = false,
      onKeyDown
    } = this.props;

    return (
      <InputControlContext
        componentId={this.props.componentId}
        className={this.props.className}
        label={this.props.label}
        info={this.props.info}
        infoLong={this.props.infoLong}
        validation={this.props.validation}
        readOnly={readOnly}
        showAIChatIcon={showAIChatIcon}
      >
        <div className="input-wrapper">
          <div className="input-group">
            <DebounceTextarea
              placeholder={placeholder}
              onChange={this.props.onChange}
              onFocus={this.onFocus}
              focus={focus}
              readOnly={readOnly}
              value={value}
              autoExpandable={autoExpandable}
              maxLength={maxLength}
              rows={rows}
              wait={wait}
              onBlur={this.onBlur}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
      </InputControlContext>
    );
  }
}
